import { useState, useEffect, useCallback } from 'react';
import { db, realtime } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export const useTopups = (options = {}) => {
  const { user } = useAuth();
  const [topups, setTopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: options.perPage || 10,
    total: 0,
  });

  const fetchTopups = useCallback(async (page = 1) => {
    if (!user) return;

    try {
      setLoading(true);
      const offset = (page - 1) * pagination.perPage;

      const { data, error, count } = await db.topups.getAll(user.id, {
        limit: pagination.perPage,
        offset,
        status: options.status,
      });

      if (error) throw error;

      setTopups(data || []);
      setPagination(prev => ({
        ...prev,
        page,
        total: count || data?.length || 0,
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user, pagination.perPage, options.status]);

  useEffect(() => {
    fetchTopups();
  }, [fetchTopups]);

  useEffect(() => {
    if (!user) return;

    const channel = realtime.subscribeToTopups(user.id, (payload) => {
      if (payload.eventType === 'INSERT') {
        setTopups(prev => [payload.new, ...prev]);
      } else if (payload.eventType === 'UPDATE') {
        setTopups(prev =>
          prev.map(t => (t.id === payload.new.id ? payload.new : t))
        );
      } else if (payload.eventType === 'DELETE') {
        setTopups(prev => prev.filter(t => t.id !== payload.old.id));
      }
    });

    return () => {
      realtime.unsubscribe(channel);
    };
  }, [user]);

  const createTopup = async (amount, paymentType = 'cryptocurrency') => {
    if (!user) return { success: false, error: 'Not authenticated' };

    try {
      const orderId = `PP-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

      const { data, error } = await db.topups.create({
        user_id: user.id,
        order_id: orderId,
        amount,
        status: 'pending',
        payment_type: paymentType,
        expires_at: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
      });

      if (error) throw error;

      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const getTopup = async (topupId) => {
    try {
      const { data, error } = await db.topups.getById(topupId);
      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const goToPage = (page) => {
    fetchTopups(page);
  };

  const setPerPage = (perPage) => {
    setPagination(prev => ({ ...prev, perPage }));
  };

  return {
    topups,
    loading,
    error,
    pagination,
    createTopup,
    getTopup,
    goToPage,
    setPerPage,
    refresh: () => fetchTopups(pagination.page),
  };
};

export default useTopups;
